import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { dispatcherService } from '../../services/dispatcherService';
import { fleetService } from '../../services/fleetService';
import DispatcherStatusBadge from '../../components/dispatchers/DispatcherStatusBadge';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import { Search, Filter, Eye, UserPlus, Edit, Trash, AlertTriangle } from 'lucide-react';

const DispatchersPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [dispatchers, setDispatchers] = useState([]);
    const [fleets, setFleets] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [totalDispatchers, setTotalDispatchers] = useState(0);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, dispatcherId: null });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10
    });
    const [filters, setFilters] = useState({
        status: '',
        fleet_id: '',
        search: ''
    });

    useEffect(() => {
        fetchDispatchers();
        fetchFleets();
    }, [pagination.currentPage, filters]);

    const fetchDispatchers = async () => {
        setIsLoading(true);
        try {
            const { data, meta } = await dispatcherService.getDispatchers({
                page: pagination.currentPage,
                limit: pagination.perPage,
                ...filters
            });

            setDispatchers(data);
            setTotalDispatchers(meta.total);
        } catch (error) {
            console.error('Error fetching dispatchers:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchFleets = async () => {
        try {
            const fleetsData = await fleetService.getFleets();
            setFleets(fleetsData);
        } catch (error) {
            console.error('Error fetching fleets:', error);
        }
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, search: value }));
    };

    const handleFleetChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, fleet_id: value }));
    };

    const handleStatusChange = (e) => {
        const value = e.target.value;
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleFilterReset = () => {
        setFilters({
            status: '',
            fleet_id: '',
            search: ''
        });
        setShowFilters(false);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
    };

    const handleViewDispatcher = (dispatcherId) => {
        navigate(`/dispatchers/${dispatcherId}`);
    };

    const handleEditDispatcher = (dispatcherId) => {
        navigate(`/dispatchers/${dispatcherId}/edit`);
    };

    const confirmDelete = (dispatcherId) => {
        setDeleteConfirmation({ show: true, dispatcherId });
    };

    const handleDeleteDispatcher = async () => {
        if (!deleteConfirmation.dispatcherId) return;

        try {
            await dispatcherService.deleteDispatcher(deleteConfirmation.dispatcherId);
            setDeleteConfirmation({ show: false, dispatcherId: null });
            fetchDispatchers(); // 重新加載調度員列表
        } catch (error) {
            console.error('Error deleting dispatcher:', error);
        }
    };

    const columns = [
        {
            header: '姓名',
            accessor: 'full_name',
            cell: (row) => (
                <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-200 rounded-full mr-3 overflow-hidden">
                        {row.avatar_url && (
                            <img src={row.avatar_url} alt={row.full_name} className="w-full h-full object-cover" />
                        )}
                    </div>
                    <div>
                        <p className="font-medium">{row.full_name}</p>
                        <p className="text-xs text-gray-500">{row.email}</p>
                    </div>
                </div>
            )
        },
        {
            header: '所屬車隊',
            accessor: 'fleet_name',
            cell: (row) => (
                <span>{row.fleet_id ? row.fleet_name : '全局調度員'}</span>
            )
        },
        {
            header: '聯絡電話',
            accessor: 'phone',
            cell: (row) => (
                <span>{row.phone}</span>
            )
        },
        {
            header: '狀態',
            accessor: 'status',
            cell: (row) => (
                <DispatcherStatusBadge status={row.status} />
            )
        },
        {
            header: '建立時間',
            accessor: 'created_at',
            cell: (row) => (
                <span className="text-sm text-gray-600">
                    {new Date(row.created_at).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </span>
            )
        },
        {
            header: '操作',
            accessor: 'actions',
            cell: (row) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleViewDispatcher(row.dispatcher_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="查看詳情"
                    >
                        <Eye className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handleEditDispatcher(row.dispatcher_id)}
                        className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                        title="編輯調度員"
                    >
                        <Edit className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => confirmDelete(row.dispatcher_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title="刪除調度員"
                    >
                        <Trash className="w-5 h-5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">調度員管理</h1>
                <button
                    onClick={() => navigate('/dispatchers/new')}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                >
                    <UserPlus className="w-5 h-5 mr-2" />
                    新增調度員
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex-1 max-w-md relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                                placeholder="搜尋調度員姓名或Email..."
                                value={filters.search}
                                onChange={handleSearch}
                            />
                        </div>

                        <select
                            className="border border-gray-300 rounded-md px-3 py-2"
                            value={filters.fleet_id}
                            onChange={handleFleetChange}
                        >
                            <option value="">所有車隊</option>
                            {fleets.map(fleet => (
                                <option key={fleet.fleet_id} value={fleet.fleet_id}>
                                    {fleet.fleet_name}
                                </option>
                            ))}
                        </select>

                        <select
                            className="border border-gray-300 rounded-md px-3 py-2"
                            value={filters.status}
                            onChange={handleStatusChange}
                        >
                            <option value="">所有狀態</option>
                            <option value="active">在職</option>
                            <option value="inactive">停職</option>
                            <option value="suspended">暫停</option>
                            <option value="on_leave">請假中</option>
                        </select>

                        {(filters.status !== '' || filters.search !== '' || filters.fleet_id !== '') && (
                            <button
                                onClick={handleFilterReset}
                                className="text-blue-500 hover:underline text-sm"
                            >
                                重置篩選
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <>
                        <DataTable
                            columns={columns}
                            data={dispatchers}
                            emptyMessage="無符合條件的調度員"
                        />
                        <div className="p-4 border-t border-gray-200">
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalItems={totalDispatchers}
                                perPage={pagination.perPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* 刪除確認對話框 */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-start mb-4">
                            <div className="flex-shrink-0 mt-0.5">
                                <AlertTriangle className="h-6 w-6 text-red-500" />
                            </div>
                            <div className="ml-3">
                                <h3 className="text-lg font-medium text-gray-900">確認刪除</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    您確定要刪除此調度員嗎？此操作無法恢復，並會移除該調度員的所有權限。
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setDeleteConfirmation({ show: false, dispatcherId: null })}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                取消
                            </button>
                            <button
                                onClick={handleDeleteDispatcher}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                            >
                                確認刪除
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DispatchersPage;